// 13 botanical ingredients for MAN 360.
// We deliberately do NOT rely on external photo services — instead every card renders
// a premium hand-drawn botanical composition (via BotanicalIcon SVG) with a warm sepia
// gradient. This looks distinctly luxurious and never breaks.

export const INGREDIENTS = [
  {
    id: "shilajit",
    name: "Shilajit",
    latin: "Asphaltum punjabianum",
    category: "Himalayan Mineral Resin",
    iconVariant: "mineral",
    accent: "#3E3629",
    short: "A dense Himalayan resin traditionally used as a rasayana.",
    note:
      "Uncovered from high-altitude Himalayan crevices, Shilajit is regarded in Ayurveda as a rasayana — a preparation associated with vitality, resilience and everyday balance. Rich in naturally occurring fulvic acid, humic acid and trace minerals.",
    tradition:
      "Referenced across Charaka Samhita and Sushruta Samhita, classical Ayurveda considers Shilajit an especially considered ingredient — often described as capable of supporting nearly every rasayana preparation.",
  },
  {
    id: "ashwagandha",
    name: "Ashwagandha",
    latin: "Withania somnifera",
    category: "Adaptogenic Root",
    iconVariant: "root",
    accent: "#8B5A2B",
    short: "The 'strength of a stallion' — a classical Ayurvedic root.",
    note:
      "Ashwagandha root has been used across classical Indian wellness traditions for centuries. Its Sanskrit name evokes the strength of a stallion, and it is considered a supportive daily botanical for calm, steady energy.",
    tradition:
      "One of the most extensively researched Ayurvedic adaptogens, considered a rasayana in classical texts.",
  },
  {
    id: "safed-musli",
    name: "Safed Musli",
    latin: "Chlorophytum borivilianum",
    category: "White Root",
    iconVariant: "tuber",
    accent: "#B8A683",
    short: "A prized white-root botanical from India's forests.",
    note:
      "A cherished Ayurvedic tonic classically referred to as 'divya aushadhi' — a divine herb. Traditionally regarded as a nourishing daily botanical in classical formulations.",
    tradition:
      "Referenced in classical Ayurvedic texts as a considered ingredient in restorative and nourishing preparations.",
  },
  {
    id: "shatavari",
    name: "Shatavari",
    latin: "Asparagus racemosus",
    category: "Tuberous Root",
    iconVariant: "tuber",
    accent: "#A08A5F",
    short: "The 'queen of herbs' — revered across classical Ayurveda.",
    note:
      "Shatavari is one of the most revered botanicals in Ayurveda. Traditionally used to support gentle nourishment, balance and everyday wellness across all life stages.",
    tradition:
      "Considered a saumya (gentle) rasayana in Ayurveda — a supportive daily botanical across classical tradition.",
  },
  {
    id: "gokshura",
    name: "Gokshura",
    latin: "Tribulus terrestris",
    category: "Botanical Fruit",
    iconVariant: "berry",
    accent: "#8A6E1F",
    short: "A hardy small berry historically used in Ayurveda and Siddha.",
    note:
      "Gokshura grows across arid Indian landscapes and has been classically used across Ayurveda and Siddha wellness traditions as a supportive daily botanical.",
    tradition:
      "Classically referenced in the Sushruta Samhita as an ingredient of considered daily preparations.",
  },
  {
    id: "kauch-beej",
    name: "Kauch Beej",
    latin: "Mucuna pruriens",
    category: "Velvet Bean Pod",
    iconVariant: "seedpod",
    accent: "#5A4A2F",
    short: "The velvet bean — a classical rasayana botanical.",
    note:
      "Kauch Beej, the velvet bean, has been referenced across classical Ayurvedic texts as a considered ingredient in daily restorative rasayana preparations.",
    tradition:
      "Historically considered a considered daily rasayana botanical across Indian wellness traditions.",
  },
  {
    id: "guduchi",
    name: "Guduchi (Giloy)",
    latin: "Tinospora cordifolia",
    category: "Climbing Vine",
    iconVariant: "vine",
    accent: "#2F5A38",
    short: "Known in Ayurveda as Amrita — 'the nectar herb.'",
    note:
      "Guduchi's Sanskrit name Amrita means 'the nectar of immortality' — a testament to the reverence with which classical Ayurveda regarded this climbing vine.",
    tradition:
      "One of the three great amrita botanicals classically referenced in Ayurveda.",
  },
  {
    id: "vidarikand",
    name: "Vidarikand",
    latin: "Pueraria tuberosa",
    category: "Tuberous Root",
    iconVariant: "tuber",
    accent: "#96825A",
    short: "A large tuberous root long-referenced in Ayurveda.",
    note:
      "Vidarikand is a large tuber that has been classically referenced in Ayurvedic texts as a considered ingredient in traditional restorative formulations.",
    tradition:
      "Referenced across the Bhavaprakasha and other classical Ayurvedic pharmacopoeia.",
  },
  {
    id: "tulsi",
    name: "Tulsi (Holy Basil)",
    latin: "Ocimum sanctum",
    category: "Sacred Herb",
    iconVariant: "tulsi",
    accent: "#3A6E42",
    short: "The sacred basil of Indian households.",
    note:
      "Tulsi is arguably the most reverentially treated herb in the Indian tradition — a plant that quite literally sits at the centre of Indian homes.",
    tradition:
      "Classical texts and folk tradition alike regard Tulsi as a considered daily adaptogen.",
  },
  {
    id: "arjun-bark",
    name: "Arjun Bark",
    latin: "Terminalia arjuna",
    category: "Sacred Tree Bark",
    iconVariant: "bark",
    accent: "#5F4830",
    short: "Bark of the Arjuna tree, a considered botanical.",
    note:
      "The bark of the sacred Arjuna tree has been classically referenced in Ayurvedic texts. The tree itself features across Indian literary and cultural tradition.",
    tradition:
      "Extensively referenced across Ayurvedic classical texts including the Charaka Samhita.",
  },
  {
    id: "pippali",
    name: "Pippali (Long Pepper)",
    latin: "Piper longum",
    category: "Warming Spike",
    iconVariant: "spike",
    accent: "#6E3A1E",
    short: "A warming long pepper prized across traditions.",
    note:
      "Long Pepper has been referenced across Ayurvedic, Unani and Siddha traditions — a warming companion herb in considered daily formulations.",
    tradition:
      "One of the three ingredients of the classical Trikatu formulation in Ayurveda.",
  },
  {
    id: "punarnava",
    name: "Punarnava",
    latin: "Boerhavia diffusa",
    category: "Monsoon Herb",
    iconVariant: "rootflower",
    accent: "#7A4A5F",
    short: "'To renew again' — a considered monsoon herb.",
    note:
      "Punarnava's Sanskrit name means 'that which renews itself' — the plant famously appears with the first monsoon rains. Classically regarded as supporting the body's daily rhythms.",
    tradition:
      "Extensively referenced across Ayurvedic pharmacopoeia including the Bhavaprakasha Nighantu.",
  },
  {
    id: "mulethi",
    name: "Mulethi (Licorice Root)",
    latin: "Glycyrrhiza glabra",
    category: "Sweet Root",
    iconVariant: "bark",
    accent: "#8B6F1E",
    short: "A naturally sweet root, considered across traditions.",
    note:
      "Mulethi is a naturally sweet root that has been classically referenced across Ayurvedic and Unani traditions as a considered harmonising ingredient in daily preparations.",
    tradition:
      "One of the most extensively referenced botanicals across classical Ayurvedic and Unani texts.",
  },
];

export const HERO_INGREDIENTS = [
  "shilajit",
  "ashwagandha",
  "safed-musli",
  "shatavari",
  "gokshura",
  "kauch-beej",
];
